import { Box, Grid, GridProps, InternalStandardProps } from "@mui/material";
import { styled } from "@mui/material/styles";
import { Content } from "JS/Models";
import { AppTypography } from "JS/React/Components/AppTypography";
import FallbackWaveform from "Images/Content/FallbackWaveform.png";
import { RecommendationBtn } from "./RecommendationsDialog";
import { AppCircularProgress } from "JS/React/Components/Progress/AppCircularProgress";

export interface RecommendationListItemProps
    extends InternalStandardProps<GridProps> {
    audio: Content;
    thumbnail: string;
    onBtnClick: (action: RecommendationBtn) => void;
    redeeming: boolean;
}

const StyledRoot = styled(Grid)(({ theme }) => ({
    padding: theme.spacing(2, 1.5),
}));

const StyledGridCenterItem = styled(Grid)(({ theme }) => ({
    padding: theme.spacing(0, 1),
}));

const StyledTitle = styled(AppTypography)(({ theme }) => ({
    fontSize: "18px",
    marginBottom: "5px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    color: theme.palette.grey[600],
}));

const StyledSkuId = styled(AppTypography)(({ theme }) => ({
    fontSize: "14px",
    color: theme.palette.grey[500],
}));

const StyledDescription = styled(AppTypography)(({ theme }) => ({
    fontSize: "12px",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    color: theme.palette.grey[600],
}));

const StyledThumbnail = styled("img")({
    width: "60px",
    height: "auto",
    objectFit: "contain",
});

const StyledActionText = styled(AppTypography)(({ theme }) => ({
    marginLeft: theme.spacing(1),
    color: theme.palette.grey[600],
}));

const StyledActionProgress = styled(AppCircularProgress)(({ theme }) => ({
    marginLeft: theme.spacing(1),
}));

export const RecommendationListItem = (props: RecommendationListItemProps) => {
    const {
        className,
        thumbnail,
        audio: content,
        redeeming,
        onBtnClick,
        ...rest
    } = props;
    const { btnText, showCredits } = useVariables(content);

    const onAction = (e) => {
        e.stopPropagation();
        onBtnClick(btnText);
    };

    return (
        <StyledRoot container className={className} {...rest}>
            <Grid
                item
                xs={2}
                display="flex"
                justifyContent="center"
                alignItems={"center"}
            >
                <StyledThumbnail
                    src={thumbnail || FallbackWaveform}
                    alt="thumbnail"
                />
            </Grid>
            <StyledGridCenterItem item xs={10}>
                <Box display={"flex"} justifyContent={"space-between"}>
                    <StyledTitle>{content.title}</StyledTitle>
                    {redeeming && btnText === "Redeem" ? (
                        <StyledActionProgress loaderSize="small" />
                    ) : (
                        <StyledActionText
                            fontWeight={"bold"}
                            variant="body1"
                            onClick={onAction}
                        >
                            {btnText}
                        </StyledActionText>
                    )}
                </Box>
                <StyledSkuId>
                    {content.sku_id} | {"AUDIO"}{" "}
                    {showCredits && <>| {content.number_of_tokens} credit(s)</>}
                </StyledSkuId>
                <StyledDescription>{content.description}</StyledDescription>
            </StyledGridCenterItem>
        </StyledRoot>
    );
};

const useVariables = (content: Content) => {
    const isPlayable =
        content.is_purchased || content.isReceived || content.publish_free;

    const btnText: RecommendationBtn = isPlayable
        ? "Play"
        : content.is_redeemable_by_tokens
        ? "Redeem"
        : "Buy";

    const showCredits = !isPlayable && content.is_redeemable_by_tokens;

    return {
        isPlayable,
        btnText,
        showCredits,
    };
};
